import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronRight, FileText } from 'lucide-react';
import { Footer } from '../components/sections/Footer';
import { useLenis } from '../lib/useLenis';

// Section wrapper for individual legal chapters
export const LegalSection = ({ id, title, children }) => (
    <section id={id} data-testid={`legal-section-${id}`} className="scroll-mt-32 mb-14 md:mb-20">
        <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-bone-100 mb-6">
            {title}
        </h2>
        <div className="space-y-4 font-body text-[15px] md:text-[16px] leading-relaxed text-bone-300 max-w-3xl">
            {children}
        </div>
    </section>
);

// Sticky Table of Contents (desktop)
const TableOfContents = ({ sections }) => {
    const [active, setActive] = useState(sections[0]?.id);
    useEffect(() => {
        const ids = sections.map((s) => s.id);
        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visible[0]) setActive(visible[0].target.id);
            },
            { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
        );
        ids.forEach((id) => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, [sections]);
    return (
        <nav
            aria-label="Table of contents"
            data-testid="legal-toc"
            className="hidden lg:block sticky top-28 self-start col-span-4"
        >
            <div className="eyebrow mb-4">Contents</div>
            <ul className="space-y-2 max-h-[calc(100vh-9rem)] overflow-y-auto pr-4 -mr-4">
                {sections.map((s, i) => (
                    <li key={s.id}>
                        <a
                            href={`#${s.id}`}
                            data-testid={`legal-toc-${s.id}`}
                            className={`group flex items-baseline gap-3 py-1 transition-colors ${
                                active === s.id ? 'text-brand-red' : 'text-bone-400 hover:text-bone-100'
                            }`}
                        >
                            <span className="font-mono text-[9px] tracking-[0.28em] w-6 shrink-0 opacity-70">{String(i + 1).padStart(2, '0')}</span>
                            <span className="font-body text-[13px] leading-snug">{s.title}</span>
                        </a>
                    </li>
                ))}
            </ul>
        </nav>
    );
};

// Legal page layout
export const LegalPage = ({
    kind, // 'terms' | 'privacy'
    title,
    lede,
    effectiveDate,
    lastUpdated,
    sections,
    children,
}) => {
    useLenis();
    const mainRef = useRef(null);
    return (
        <div className="relative min-h-screen bg-ink-900 text-bone-100" data-testid={`legal-page-${kind}`}>
            {/* Simple top nav — return home */}
            <header className="sticky top-0 z-40 bg-ink-900/85 border-b border-white/5">
                <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-4 flex items-center justify-between">
                    <Link
                        to="/"
                        data-testid="legal-nav-home"
                        className="flex items-center gap-3 group shrink-0"
                        aria-label="MSD Painting Services home"
                    >
                        <span className="inline-flex items-center justify-center bg-bone-100 rounded-sm p-1.5">
                            <img
                                src="https://customer-assets-39nsmqrw.emergentagent.net/job_crafted-spaces-21/artifacts/9qjq48qm_Image.jpeg"
                                alt="MSD Painting Services"
                                className="h-8 w-auto object-contain"
                                loading="eager"
                            />
                        </span>
                        <div className="hidden sm:block leading-none">
                            <div className="font-display text-[14px] tracking-tight text-bone-100">MSD Painting</div>
                            <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-bone-500 mt-1">Rio Rancho · NM</div>
                        </div>
                    </Link>
                    <Link
                        to="/"
                        data-testid="legal-back-link"
                        className="btn-pill btn-pill--ghost"
                    >
                        <ArrowLeft size={14} strokeWidth={1.5} />
                        Back to site
                    </Link>
                </div>
            </header>

            {/* Legal-themed hero */}
            <section className="relative overflow-hidden border-b border-white/10">
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 opacity-30"
                    style={{ background: 'radial-gradient(ellipse at 15% 30%, rgba(42,107,242,0.20) 0%, rgba(42,107,242,0) 55%), radial-gradient(ellipse at 85% 70%, rgba(230,57,70,0.18) 0%, rgba(230,57,70,0) 55%)' }}
                />
                <div className="relative mx-auto max-w-[1600px] px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-24">
                    {/* Breadcrumb */}
                    <nav aria-label="Breadcrumb" data-testid="legal-breadcrumb" className="mb-10 flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-bone-400">
                        <Link to="/" className="hover:text-bone-100 transition-colors">MSD</Link>
                        <ChevronRight size={12} className="opacity-60" />
                        <span className="text-bone-400">Legal</span>
                        <ChevronRight size={12} className="opacity-60" />
                        <span className="text-bone-100">{title}</span>
                    </nav>

                    <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
                        <div className="col-span-12 md:col-span-8">
                            <div className="mb-6 flex items-center gap-3">
                                <span className="inline-flex items-center justify-center w-9 h-9 rounded-sm border border-brand-blue/60 text-brand-blue">
                                    <FileText size={16} strokeWidth={1.5} />
                                </span>
                                <span className="eyebrow">Chapter · Legal</span>
                            </div>
                            <motion.h1
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
                                className="font-display font-black uppercase tracking-[-0.03em] leading-[0.88] text-bone-100 text-[12vw] md:text-[8vw] lg:text-[6.4rem]"
                            >
                                {title}
                            </motion.h1>
                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.9, delay: 0.15 }}
                                className="mt-8 max-w-2xl font-body text-lg md:text-xl font-light text-bone-200 leading-relaxed"
                            >
                                {lede}
                            </motion.p>
                        </div>
                        <div className="col-span-12 md:col-span-4 md:pl-8 md:border-l md:border-white/10 flex flex-col gap-6">
                            <div>
                                <div className="eyebrow mb-2">Effective</div>
                                <div className="font-mono text-sm text-bone-100" data-testid="legal-effective-date">{effectiveDate}</div>
                            </div>
                            <div>
                                <div className="eyebrow mb-2">Last Updated</div>
                                <div className="font-mono text-sm text-bone-100" data-testid="legal-last-updated">{lastUpdated}</div>
                            </div>
                            <div>
                                <div className="eyebrow mb-2">Jurisdiction</div>
                                <div className="font-mono text-sm text-bone-100">New Mexico, USA</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Body */}
            <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-20 md:py-28">
                <div className="grid grid-cols-12 gap-6 md:gap-10">
                    <TableOfContents sections={sections} />
                    <main ref={mainRef} data-testid="legal-content" className="col-span-12 lg:col-span-8">
                        {children}

                        {/* Cross-link to the other legal doc */}
                        <div className="mt-16 pt-10 border-t border-white/10 flex flex-wrap items-center gap-4 justify-between">
                            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-bone-400">
                                Continue reading
                            </div>
                            <Link
                                to={kind === 'terms' ? '/privacy' : '/terms'}
                                data-testid="legal-cross-link"
                                className="btn-pill btn-pill--ghost"
                            >
                                {kind === 'terms' ? 'Privacy Policy' : 'Terms & Conditions'}
                                <ChevronRight size={14} strokeWidth={1.5} />
                            </Link>
                        </div>
                    </main>
                </div>
            </div>

            <Footer />
        </div>
    );
};
